import {
  Component,
  OnInit,
  OnDestroy,
  ElementRef,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, Subscription } from 'rxjs';
import { selectUserMenuList } from 'src/app/store/userMenuList/userMenuList.selector';
import { COMMON_SVG_IMG_PATH } from 'src/app/shared/common.constant';

interface BreadcrumbSubItem {
  sub_menu_name: string;
  route_link: string;
}

interface BreadcrumbMenu {
  menu_name: string;
  menu_code: string;
  route_link?: string;
  items?: BreadcrumbSubItem[];
}

@Component({
  selector: 'app-breadcrumb',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './breadcrumb.component.html',
  styleUrls: ['./breadcrumb.component.scss'],
})
export class BreadcrumbComponent implements OnInit, OnDestroy {
  navData: BreadcrumbMenu[] = [];
  activeMenu: BreadcrumbMenu | null = null;
  activeSubItem: BreadcrumbSubItem | null = null;
  isDropdownOpen = false;
  private readonly subs: Subscription[] = [];
  svgPath: string = COMMON_SVG_IMG_PATH;

  constructor(
    private readonly router: Router,
    private readonly store: Store,
    private readonly elRef: ElementRef
  ) {}

  ngOnInit(): void {
    this.subs.push(
      this.store.select(selectUserMenuList).subscribe((menuList) => {
        this.navData = menuList ?? [];
        this.updateBreadcrumb(this.router.url);
      })
    );

    this.subs.push(
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe(() => this.updateBreadcrumb(this.router.url))
    );
  }

  private updateBreadcrumb(currentUrl: string): void {
    this.activeMenu = null;
    this.activeSubItem = null;
    this.isDropdownOpen = false;

    for (const menu of this.navData) {
      const matchedSub = menu.items?.find((sub) =>
        this.matchesRoute(currentUrl, sub.route_link)
      );
      if (matchedSub) {
        this.activeMenu = menu;
        this.activeSubItem = matchedSub;
        return;
      }
      if (menu.route_link && this.matchesRoute(currentUrl, menu.route_link)) {
        this.activeMenu = menu;
        return;
      }
    }
  }

  private matchesRoute(currentUrl: string, routeLink: string): boolean {
    const index = currentUrl.indexOf(routeLink);
    if (index === -1) {
      return false;
    }
    const nextChar = currentUrl.charAt(index + routeLink.length);
    return nextChar === '' || nextChar === '/' || nextChar === '?';
  }

  /**
   * "Catalogue" is a mode marker on the Synopsis menu (see sidenav/synopsis
   * components), not a real navigable report — exclude it from the dropdown.
   */
  get visibleSubItems(): BreadcrumbSubItem[] {
    return (this.activeMenu?.items ?? []).filter(
      (item) => !this.isCatalogueReportItem(item)
    );
  }

  private isCatalogueReportItem(item: BreadcrumbSubItem): boolean {
    return item.sub_menu_name?.trim().toLowerCase() === 'catalogue';
  }

  toggleDropdown(): void {
    if (this.visibleSubItems.length) {
      this.isDropdownOpen = !this.isDropdownOpen;
    }
  }

  navigateToSub(sub: BreadcrumbSubItem): void {
    this.isDropdownOpen = false;
    this.router.navigateByUrl(sub.route_link);
  }

  navigateHome(): void {
    this.router.navigate(['']);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elRef.nativeElement.contains(event.target)) {
      this.isDropdownOpen = false;
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }
}
